import type { GatewayJobSummary } from './useSegmentationGateway';

import type { BatchResult, SegmentationDialogEntry, SegmentationSettings } from '@/src/components/segmentation/segmentation-typing';

import type { AnalysisResult } from '@/src/store/analysis/data-typing.ts';

import { computed, nextTick, ref, watch } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { SEGMENTATION_SETTINGS_SCHEMA } from '@/src/components/segmentation/segmentation-typing';
import { useAlert } from '@/src/composables/useAlert';
import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { useAnalysisStore } from '@/src/store/analysis';
import { useSegmentationSettingsStore } from '@/src/store/contours/contour-segmentation';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { Orientation } from '@/src/store/dicom-web/classification-typing';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { seriesClassification } from '@/src/store/dicom-web/SeriesClassification';
import { useUpdatedReportStore } from '@/src/store/report-store';

import { session } from '@/src/supabase';

import { getMessage } from '../utils/exception';
import { useSegmentationGateway } from './useSegmentationGateway';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ViewerSegmentationSummary {
  viewerID: string;
  orientation: Orientation;
  imageCount: number;
  seriesIdx: number;
  sliceIdx: number;
}

// ── Singleton state (shared across all callers) ────────────────────────────────

const showDialog = ref(false);
const viewerSummaries = ref<ViewerSegmentationSummary[]>([]);
const isStartingSegmentation = ref(false);

// Per-viewer selections
const selectedOrientations = ref<Record<string, Orientation>>({});
const selectedImageScope = ref<Record<string, string>>({});

// Tracks which viewers had imageUIDs pre-provided by the caller
const preProvidedViewerUIDs = ref<Record<string, string[]>>({});

// Tracks whether duplicate viewerIDs were passed into trigger()
const duplicateViewerIDDetected = ref(false);

// Model selection
const modelSettingOptions = SEGMENTATION_SETTINGS_SCHEMA.SAX_MODEL_NAME?.optionsRaw;

const selectedModel = ref<SegmentationSettings['SAX_MODEL_NAME']>('auto');

// Jobs Hub state
const showJobsHub = ref(false);
const jobsList = ref<GatewayJobSummary[]>([]);
const isLoadingJobs = ref(false);
const applyingJobId = ref<string | null>(null);

// Available orientations for the dropdown
const availableOrientations = [
  Orientation.Undefined,
  Orientation.Sax,
  Orientation.Lax2CV,
  Orientation.Lax3CV,
  Orientation.Lax4CV,
];

// ── Computed (module-level, derived from singleton refs) ────────────────────

const hasSummaries = computed(() => viewerSummaries.value.length > 0);

const uniqueViewerSummaries = computed(() => {
  const seen = new Set<string>();
  return viewerSummaries.value.filter((summary) => {
    const key = `${summary.seriesIdx}-${summary.sliceIdx}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const hasUndefinedOrientation = computed(() =>
  uniqueViewerSummaries.value.some(
    summary => summary.orientation === Orientation.Undefined,
  ),
);

const hasDuplicateSeriesSlice = computed(
  () => uniqueViewerSummaries.value.length < viewerSummaries.value.length,
);

const hasDuplicateViewerID = computed(() => duplicateViewerIDDetected.value);

// ── Composable ─────────────────────────────────────────────────────────────────

export function useSegmentationDialog() {
  // ── Dependencies (resolved at call-time, inside setup context) ───────────
  const backendSegmentation = useBackendSegmentation();
  const currentStudyStore = useCurrentStudyStore();
  const viewportActions = useViewportActions();
  const alert = useAlert();
  const segmentationSettingsStore = useSegmentationSettingsStore();
  const viewportStateStore = useViewportStateStore();
  const analysisStore = useAnalysisStore();

  const isSegmentationActive = computed(
    () => backendSegmentation.isActive.value,
  );
  const segmentationRunningAlertId = ref<string | null>(null);

  // Initialize model preference from store on first composable use
  const storedSettings = segmentationSettingsStore.getSettings();
  if (storedSettings.SAX_MODEL_NAME) {
    selectedModel.value = storedSettings.SAX_MODEL_NAME;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Returns true if the caller pre-supplied imageUIDs for this viewer,
   * meaning the scope selector should be hidden.
   */
  function hasPreProvidedUIDs(viewerID: string): boolean {
    return viewerID in preProvidedViewerUIDs.value;
  }

  /**
   * Get the effective image UIDs for a viewer based on scope and pre-provided UIDs.
   */
  function getEffectiveImageUIDs(viewerID: string): string[] {
    // If the caller pre-supplied UIDs, use those directly
    if (hasPreProvidedUIDs(viewerID)) {
      return preProvidedViewerUIDs.value[viewerID];
    }

    // Otherwise, collect from the viewer based on selected scope
    const imageUIDsAll
      = currentStudyStore.getSliceImageUIDsbyView(viewerID) ?? [];
    if (imageUIDsAll.length === 0) return [];

    const studyObj = viewportActions.getStudyObjectByViewerId(viewerID);
    const excludedImages = studyObj?.getExcludedImages() ?? new Set<string>();
    const scope = selectedImageScope.value[viewerID] || 'Breathhold';

    if (scope === 'All in Slice') {
      return imageUIDsAll.filter((u: string) => !excludedImages.has(u));
    }

    // Breathhold (default)
    const breathhold = studyObj?.getBreathhold();
    const start = breathhold?.getStart();
    const end = breathhold?.getEnd();
    const cropped = imageUIDsAll.slice(start, end && end + 1);
    return cropped.filter((u: string) => !excludedImages.has(u));
  }

  /**
   * Get breathhold range text for a viewer (for template display).
   */
  function getBreathholdRange(viewerID: string): string {
    try {
      const studyObj = viewportActions.getStudyObjectByViewerId(viewerID);
      if (!studyObj) return '';
      const bh = studyObj.getBreathhold();
      return `${bh.getStart() + 1} - ${bh.getEnd() + 1}`;
    }
    catch {
      return '';
    }
  }

  // ── Core methods ────────────────────────────────────────────────────────────

  /**
   * Build viewer summaries from the list of deduplicated viewerIDs.
   */
  function buildViewerSummaries(viewerIDs: string[]) {
    viewerSummaries.value = viewerIDs.map((viewerID) => {
      const { seriesIdx, sliceIdx }
        = viewportStateStore.getSelectedImageIndices(viewerID);

      const includedUIDs = getEffectiveImageUIDs(viewerID);

      // If there are no images and no pre-provided UIDs,
      // this viewer is likely not properly initialized.
      if (includedUIDs.length === 0 && !hasPreProvidedUIDs(viewerID)) {
        console.warn(
          `No images found for viewer ${viewerID}. Ensure viewer is displaying image data before segmentation.`,
        );
      }

      const orientation
        = seriesClassification.getClassificationByViewport(viewerID)
          ?.orientation ?? Orientation.Undefined;

      // Initialize per-viewer selections (only if not already set)
      if (!(viewerID in selectedOrientations.value)) {
        selectedOrientations.value[viewerID] = orientation;
      }
      if (!(viewerID in selectedImageScope.value)) {
        selectedImageScope.value[viewerID] = 'Breathhold';
      }

      return {
        viewerID,
        orientation,
        imageCount: includedUIDs.length,
        seriesIdx,
        sliceIdx,
      };
    });
  }

  //  /**
  //  * Abort active segmentation and close the dialog.
  //  */
  function abort() {
    backendSegmentation.abort();
    showDialog.value = false;
  }

  /**
   * Main entry point — open the dialog for a set of viewer entries.
   *
   * @example
   * const segDialog = useSegmentationDialog();
   * segDialog.trigger([
   *   { viewerID: 'ContoursLeft', imageUIDs: ['uid1', 'uid2'] },
   *   { viewerID: 'ContoursRight' },
   * ]);
   */
  function trigger(entries: SegmentationDialogEntry[]) {
    // Reset state
    selectedOrientations.value = {};
    selectedImageScope.value = {};
    preProvidedViewerUIDs.value = {};
    duplicateViewerIDDetected.value = false;

    // Deduplicate by viewerID — keep only the first occurrence
    const seen = new Set<string>();
    const dedupedEntries: SegmentationDialogEntry[] = [];

    for (const entry of entries) {
      if (seen.has(entry.viewerID)) {
        duplicateViewerIDDetected.value = true;
        continue;
      }
      seen.add(entry.viewerID);
      dedupedEntries.push(entry);
    }

    // Record which viewers have pre-provided imageUIDs

    for (const entry of dedupedEntries) {
      if (entry.imageUIDs && entry.imageUIDs.length > 0) {
        preProvidedViewerUIDs.value[entry.viewerID] = entry.imageUIDs;
      }
    }

    const viewerIDs = dedupedEntries.map(e => e.viewerID);
    buildViewerSummaries(viewerIDs);
    showDialog.value = true;
  }

  /**
   * Validate everything and start the segmentation jobs.
   */
  async function startSegmentation() {
    // Validate orientations
    const hasUndefined = uniqueViewerSummaries.value.some((summary) => {
      const o
        = selectedOrientations.value[summary.viewerID] ?? Orientation.Undefined;
      return o === Orientation.Undefined;
    });
    if (hasUndefined) {
      alert.error(
        'Please define an orientation classification for every selected viewer before segmentation.',
      );
      return;
    }

    // Update global model selection
    segmentationSettingsStore.updateSettings({
      SAX_MODEL_NAME: selectedModel.value,
    });

    isStartingSegmentation.value = true;
    try {
      // Build jobs only for unique viewers (no duplicate series/slice)
      const jobs = uniqueViewerSummaries.value.map((summary) => {
        const vid = summary.viewerID;
        const includeUIDs = getEffectiveImageUIDs(vid);

        return {
          viewerID: vid,
          imageUIDs: includeUIDs,
          orientation: selectedOrientations.value[vid],
        };
      });

      void backendSegmentation.segmentJobs(jobs, false, true);
      showDialog.value = false;
      segmentationRunningAlertId.value = alert.info(
        `Segmentation running please wait...`,
        true,
      );
    }
    catch (error: any) {
      alert.error(
        `Segmentation failed: ${getMessage(error)}`,
      );
    }
    finally {
      isStartingSegmentation.value = false;
    }
  }

  /**
   * Update orientation for a viewer, persist the classification, and rebuild summaries.
   */
  function handleOrientationUpdate(
    viewerID: string,
    newOrientation: Orientation,
  ) {
    selectedOrientations.value[viewerID] = newOrientation;
    seriesClassification.adjustClassificationByViewport(viewerID, {
      orientation: newOrientation,
      mode: 'Manual',
    });
    void nextTick(() => {
      const viewerIDs = viewerSummaries.value.map(s => s.viewerID);
      buildViewerSummaries(viewerIDs);
    });
  }

  // ── Watchers ────────────────────────────────────────────────────────────────

  // When the user changes the image scope for any viewer, rebuild summaries
  watch(
    selectedImageScope,
    () => {
      if (viewerSummaries.value.length > 0) {
        const viewerIDs = viewerSummaries.value.map(s => s.viewerID);
        buildViewerSummaries(viewerIDs);
      }
    },
    { deep: true },
  );

  watch(isSegmentationActive, (active) => {
    if (!active && segmentationRunningAlertId.value) {
      alert.hide(segmentationRunningAlertId.value);
      segmentationRunningAlertId.value = null;
    }
  });

  // ── Gateway Jobs Hub ────────────────────────────────────────────────────────

  const gateway = useSegmentationGateway();

  async function fetchJobsList(userId?: string) {
    const studyID = currentStudyStore.getStudyID;
    if (!studyID) {
      alert.error('No study ID found.');
      return;
    }
    isLoadingJobs.value = true;
    try {
      const res = await gateway.listJobs({
        userId,
        studyInstanceUid: studyID,
      });
      jobsList.value = res.jobs;
    }
    catch (err: any) {
      alert.error(`Failed to fetch jobs: ${getMessage(err)}`);
    }
    finally {
      isLoadingJobs.value = false;
    }
  }

  async function abortGatewayJob(jobId: string, userId?: string) {
    const activeUserId = userId || session.value?.user?.id;
    if (!activeUserId) {
      alert.error('No user ID found.');
      return;
    }
    try {
      await gateway.abortJob(jobId);
      alert.success(`Job ${jobId.substring(0, 8)}... aborted.`);
      // TODO: add toggle in UI to filter only for self
      await fetchJobsList();
    }
    catch (err: any) {
      alert.error(`Failed to abort job: ${getMessage(err)}`);
    }
  }

  function handleAnalysisResponse(responseData: AnalysisResult) {
    const biomarkerContainer = analysisStore.resetStudy();
    const analysisViewers = ['AnalysisLeft', 'AnalysisRight'];

    biomarkerContainer.populateFromResponse(responseData);

    const dynamicMaskTypes
      = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Dynamic');
    const staticMaskTypes
      = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('Static');
    const polarMapTypes
      = biomarkerContainer.getMaskTypesBasedOnBiomarkerType('PolarMap');

    // Map each analysis viewer back to the confirmed selection.
    // When duplicated, both analysis viewers get the same indices
    // (from the single processed selection).
    for (let i = 0; i < analysisViewers.length; i++) {
      const viewID = analysisViewers[i];
      const sel = viewerSummaries.value[i];

      const sourceViewerID
        = sel?.viewerID ?? viewerSummaries.value[0]?.viewerID ?? 'ContoursLeft';
      const sourceIndices
        = viewportStateStore.getSelectedImageIndices(sourceViewerID);

      viewportStateStore.setSelectedImageIndices(viewID, sourceIndices, true);

      if (staticMaskTypes[0]) {
        viewportStateStore.setSelectedMarker(
          viewID,
          'Static',
          staticMaskTypes[0],
        );
      }
      if (dynamicMaskTypes[0]) {
        viewportStateStore.setSelectedMarker(
          viewID,
          'Dynamic',
          dynamicMaskTypes[0],
        );
      }

      if (polarMapTypes[0]) {
        viewportStateStore.setSelectedMarker(
          viewID,
          'PolarMap',
          polarMapTypes[0],
        );
      }
      viewportStateStore.setShowContours(viewID, false);
    }

    const reportStore = useUpdatedReportStore();
    reportStore.resetStore();
  }

  async function applyGatewayJobResults(jobId: string) {
    applyingJobId.value = jobId;
    const t0 = performance.now();
    try {
      const tGetJob = performance.now();
      const detail = await gateway.getJob(jobId);
      console.log(`[applyGatewayJobResults] getJob: ${(performance.now() - tGetJob).toFixed(1)}ms`, detail);

      let batchResults: BatchResult[] = [];
      let analysisResponseData: AnalysisResult | undefined;

      // First check if there is a zip_url:
      if (detail.zip_url && detail.zip_url !== '') {
        const tZip = performance.now();
        const zipResult = await gateway.fetchJobZipResults(detail.zip_url);
        console.log(`[applyGatewayJobResults] fetchJobZipResults: ${(performance.now() - tZip).toFixed(1)}ms`);

        analysisResponseData = zipResult.analysisResponseData;
        batchResults = zipResult.batchResults || [];
      }

      if (detail.type === 'analysis' && analysisResponseData) {
        analysisStore.resetStudy();

        handleAnalysisResponse(analysisResponseData);
        console.log(`[applyGatewayJobResults] handleAnalysisResponse TOTAL: ${(performance.now() - t0).toFixed(1)}ms`);

        alert.success('Applied analysis job results and contours to store.');
      }
      else if (detail.type === 'init') {
        if (batchResults.length > 0) {
          backendSegmentation.addContourBatch(batchResults);
        }
        alert.success('Applied init job results to store.');
      }
      else {
        if (batchResults.length === 0) {
          alert.warning('No valid result data could be applied.');
          return;
        }
        backendSegmentation.addContourBatch(batchResults);
        alert.success(`Applied ${batchResults.length} segmentation result(s) to store.`);
      }
    }
    catch (err: any) {
      alert.error(`Failed to apply job results: ${getMessage(err)}`);
    }
    finally {
      applyingJobId.value = null;
    }
  }

  function openJobsHub(userId?: string) {
    const activeUserId = userId || session.value?.user?.id;
    if (!activeUserId) {
      alert.error('No user ID found.');
      return;
    }
    showJobsHub.value = true;
    void fetchJobsList();
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  return {
    // Singleton reactive state
    showDialog,
    showJobsHub,
    jobsList,
    isLoadingJobs,
    applyingJobId,
    viewerSummaries,
    uniqueViewerSummaries,
    selectedOrientations,
    selectedImageScope,
    selectedModel,
    isStartingSegmentation,
    isSegmentationActive,
    hasSummaries,
    hasUndefinedOrientation,
    hasDuplicateSeriesSlice,
    hasDuplicateViewerID,
    modelSettingOptions,
    availableOrientations,

    // Methods
    trigger,
    startSegmentation,
    handleOrientationUpdate,
    abort,
    hasPreProvidedUIDs,
    getBreathholdRange,
    fetchJobsList,
    abortGatewayJob,
    applyGatewayJobResults,
    openJobsHub,
  };
}
